/**
 * @fileoverview Canonical Zod schema for a single chat message within a
 * "Chat with Your Story" session. Follows the "schema is canonical, types
 * are inferred" pattern used across the codebase.
 */

import { z } from 'zod';

export const ChatRoleEnum = z.enum(['user', 'assistant', 'tool', 'system']);
export type ChatRole = z.infer<typeof ChatRoleEnum>;

/**
 * Record of a tool invocation the assistant made during a turn. Stored
 * alongside the message so the UI can render transparency indicators like
 * "Reading Chapter 4...".
 */
export const ChatToolCallSchema = z.object({
  id: z.string().describe('Unique identifier for this tool invocation.'),
  name: z.string().describe('Name of the tool that was called (e.g., "readChapter").'),
  input: z.unknown().optional().describe('Arguments passed to the tool.'),
  output: z.unknown().optional().describe('Result returned by the tool (may be omitted for storage size).'),
  startedAt: z.string().describe('ISO timestamp when the tool call began.'),
  completedAt: z.string().optional().describe('ISO timestamp when the tool call completed.'),
  error: z.string().optional().describe('Error message if the tool call failed.'),
});
export type ChatToolCall = z.infer<typeof ChatToolCallSchema>;

/**
 * A single message in a chat session.
 */
export const ChatMessageSchema = z.object({
  id: z.string().describe('Unique identifier for this message.'),
  role: ChatRoleEnum.describe('Who authored the message.'),
  content: z.string().describe('Message text content (plain or markdown).'),
  toolCalls: z.array(ChatToolCallSchema).optional().describe('Tool invocations made during this turn (assistant messages only).'),
  timestamp: z.string().describe('ISO timestamp when the message was created.'),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;
